import React from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useLanguage } from '../../contexts/LanguageContext';

const termOptions = [
  { value: 'custom', label: 'Custom' },
  { value: 'net30', label: 'Net 30', days: 30 },
  { value: 'net45', label: 'Net 45', days: 45 },
  { value: 'net60', label: 'Net 60', days: 60 },
  { value: 'net90', label: 'Net 90', days: 90 },
];

function addDays(dateString, days) {
  if (!dateString) return '';
  const dt = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return '';
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
}

export default function AdditionalFields({ notes, setNotes, business, setBusiness, number, setNumber, date, setDate, dueDate, setDueDate, paymentTerms, setPaymentTerms, customPaymentTerms, setCustomPaymentTerms }) {
  const { t } = useLanguage();
  const bd = business.bankDetails || {};
  const updateBank = (patch) => setBusiness((b) => ({ ...b, bankDetails: { ...(b.bankDetails || {}), ...patch } }));
  const updateTerms = (value) => {
    setPaymentTerms(value);
    const option = termOptions.find((t) => t.value === value);
    if (option?.days) setDueDate(addDays(date, option.days));
    if (value !== 'custom') setCustomPaymentTerms('');
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="additional-fields-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900">{t('invoiceDetailsPayment')}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>{t('invoiceNumber')}</Label>
          <Input value={number} onChange={(e) => setNumber(e.target.value)} className="font-mono" data-testid="invoice-number-input" />
        </div>
        <div>
          <Label>{t('date')}</Label>
          <Input type="date" value={date} onChange={(e) => {
            setDate(e.target.value);
            const option = termOptions.find((t) => t.value === paymentTerms);
            if (option?.days) setDueDate(addDays(e.target.value, option.days));
          }} data-testid="invoice-date-input" />
        </div>
        <div>
          <Label>{t('dueDateOptional')}</Label>
          <Input type="date" value={dueDate || ''} onChange={(e) => setDueDate(e.target.value)} data-testid="invoice-duedate-input" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>{t('paymentTerms')}</Label>
          <Select value={paymentTerms || 'custom'} onValueChange={updateTerms}>
            <SelectTrigger data-testid="payment-terms-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {termOptions.map((term) => <SelectItem key={term.value} value={term.value}>{term.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(paymentTerms || 'custom') === 'custom' && (
          <div className="md:col-span-2">
            <Label>{t('customTerms')}</Label>
            <Input value={customPaymentTerms || ''} onChange={(e) => setCustomPaymentTerms(e.target.value)} placeholder={t('customTermsPlaceholder')} data-testid="custom-payment-terms-input" />
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('bankName')}</Label>
          <Input value={bd.bankName || ''} onChange={(e) => updateBank({ bankName: e.target.value })} placeholder="HDFC Bank" data-testid="bank-name-input" />
        </div>
        <div>
          <Label>{t('accountNumber')}</Label>
          <Input value={bd.accountNo || ''} onChange={(e) => updateBank({ accountNo: e.target.value })} placeholder="50100123456789" className="font-mono" data-testid="bank-account-input" />
        </div>
        <div>
          <Label>{t('ifsc')}</Label>
          <Input value={bd.ifsc || ''} onChange={(e) => updateBank({ ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className="font-mono uppercase" data-testid="bank-ifsc-input" />
        </div>
        <div>
          <Label>{t('upiId')}</Label>
          <Input value={bd.upi || ''} onChange={(e) => updateBank({ upi: e.target.value })} placeholder="yourname@okhdfc" className="font-mono" data-testid="bank-upi-input" />
        </div>
      </div>

      <div className="mt-4">
        <Label>{t('notesTerms')}</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('notesPlaceholder')} data-testid="invoice-notes-input" />
      </div>
    </section>
  );
}
